/**
 * Translation File Processor for Client-Side Data Handling
 * 
 * This class handles file-based translation data processing in the browser,
 * providing pagination, filtering, and language column selection without 
 * server-side database queries.
 * 
 * Based on the REVISED architecture documented in:
 * docs/new features/REVIEW_PAGE_OPTIMIZATION_REVISED.md
 */

import Papa from 'papaparse'
import * as XLSX from 'xlsx'

class TranslationFileProcessor {
  constructor(settingsManager = null, jobId = null) {
    this.rawData = []
    this.processedData = []
    this.headers = []
    this.totalRows = 0
    this.isLoaded = false
    this.sourceLanguage = null
    this.targetLanguages = []
    this.allLanguages = []
    
    // Settings management
    this.settingsManager = settingsManager
    this.jobId = jobId
    
    // Pagination state - initialize from settings if available
    this.currentPage = 1
    this.itemsPerPage = this._getInitialItemsPerPage()
    
    // Filtering state
    this.visibleLanguages = []
    this.searchFilter = ''
    
    // Performance metrics
    this.loadTime = null
    this.processTime = null
  }

  /**
   * Initialize items per page from user settings
   */
  _getInitialItemsPerPage() {
    if (this.settingsManager) {
      return this.settingsManager.getItemsPerPage()
    }
    return 25 // Default fallback
  }

  /**
   * Load and process translation data from various sources
   * @param {Object} source - Data source object with type and data
   * @param {string} source.type - 'json' | 'csv' | 'excel' | 'url'
   * @param {*} source.data - File content, URL, or raw data
   * @param {Object} options - Processing options
   */
  async loadData(source, options = {}) {
    const startTime = performance.now()
    
    try {
      switch (source.type) {
        case 'json':
          await this._processJsonData(source.data)
          break
        case 'csv':
          await this._processCsvData(source.data)
          break
        case 'excel':
          await this._processExcelData(source.data)
          break
        case 'url':
          await this._loadFromUrl(source.data, options)
          break
        default:
          throw new Error(`Unsupported data source type: ${source.type}`)
      }
      
      this._detectLanguages(options)
      this._initializeVisibleLanguages()
      this._loadFromSettings() // Load settings after data is initialized
      this.isLoaded = true
      this.loadTime = performance.now() - startTime
      
      console.log(`✅ TranslationFileProcessor loaded ${this.totalRows} rows with ${this.allLanguages.length} languages in ${this.loadTime.toFixed(2)}ms`)
      
    } catch (error) {
      console.error('❌ Failed to load translation data:', error)
      throw error
    }
  }

  /**
   * Process JSON data (from T8R content endpoint)
   */
  async _processJsonData(jsonData) {
    const processStart = performance.now()
    
    if (typeof jsonData !== 'object' || jsonData === null) {
      throw new Error('Invalid JSON data format')
    }
    
    // Extract headers (column names) from JSON object
    this.headers = Object.keys(jsonData)
    
    if (this.headers.length === 0) {
      throw new Error('No data columns found in JSON')
    }
    
    // Get row count from first column
    const firstColumnData = jsonData[this.headers[0]]
    this.totalRows = Array.isArray(firstColumnData) ? firstColumnData.length : 0
    
    if (this.totalRows === 0) {
      console.warn('No data rows found')
      this.processedData = []
      return
    }
    
    // Convert column-based JSON to row-based data structure
    this.processedData = []
    for (let i = 0; i < this.totalRows; i++) {
      const rowData = { _rowIndex: i }
      this.headers.forEach((header) => {
        const columnData = jsonData[header]
        rowData[header] = Array.isArray(columnData) ? (columnData[i] || '') : ''
      })
      this.processedData.push(rowData)
    }
    
    this.processTime = performance.now() - processStart
  }

  /**
   * Process CSV data using PapaParse
   */
  async _processCsvData(csvText) {
    const processStart = performance.now()
    
    return new Promise((resolve, reject) => {
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim(),
        complete: (results) => {
          if (results.errors.length > 0) {
            console.warn('CSV parsing warnings:', results.errors)
          }
          
          this.processedData = results.data.map((row, index) => ({
            ...row,
            _rowIndex: index
          }))
          this.headers = results.meta.fields || []
          this.totalRows = this.processedData.length
          this.processTime = performance.now() - processStart
          resolve()
        },
        error: (error) => reject(new Error(`CSV parsing failed: ${error.message}`))
      })
    })
  }

  /**
   * Process Excel data using SheetJS
   */
  async _processExcelData(excelFile) {
    const processStart = performance.now()
    
    try {
      // Read Excel file
      const workbook = XLSX.read(excelFile, { type: 'array' })
      const firstSheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[firstSheetName]
      
      // Convert to JSON with header row
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
        header: 1, 
        defval: '' 
      })
      
      if (jsonData.length === 0) {
        throw new Error('Excel file is empty')
      }
      
      // Extract headers from first row
      this.headers = jsonData[0].map(header => String(header).trim())
      
      // Convert remaining rows to objects
      this.processedData = jsonData.slice(1).map((row, index) => {
        const rowData = { _rowIndex: index }
        this.headers.forEach((header, colIndex) => {
          rowData[header] = String(row[colIndex] || '').trim()
        })
        return rowData
      })
      
      this.totalRows = this.processedData.length
      this.processTime = performance.now() - processStart
      
    } catch (error) {
      throw new Error(`Excel processing failed: ${error.message}`)
    }
  }

  /**
   * Load data from URL endpoint
   */
  async _loadFromUrl(url, options = {}) {
    const response = await fetch(url, {
      headers: options.headers || {}
    })
    
    if (!response.ok) {
      throw new Error(`Failed to fetch data: ${response.statusText}`)
    }
    
    const contentType = response.headers.get('content-type') || ''
    
    if (contentType.includes('application/json')) {
      const jsonData = await response.json()
      await this._processJsonData(jsonData)
    } else if (contentType.includes('text/csv')) {
      const csvText = await response.text()
      await this._processCsvData(csvText)
    } else {
      // Try to detect format by content
      const text = await response.text()
      try {
        const jsonData = JSON.parse(text)
        await this._processJsonData(jsonData)
      } catch {
        // Fallback to CSV
        await this._processCsvData(text)
      }
    }
  }

  /**
   * Detect source and target languages from headers
   */
  _detectLanguages(options = {}) {
    // Manual language specification takes precedence
    if (options.sourceLanguage) {
      this.sourceLanguage = options.sourceLanguage
    }
    if (options.targetLanguages) {
      this.targetLanguages = [...options.targetLanguages]
      this.allLanguages = [this.sourceLanguage, ...this.targetLanguages].filter(Boolean)
      return
    }
    
    // Auto-detect from headers
    // Common patterns: "en-US", "de-DE", "source_text", "target_cs-CZ"
    const languagePattern = /([a-z]{2}-[A-Z]{2})|([a-z]{2})/
    const sourcePatterns = ['source', 'original', 'input']
    
    this.allLanguages = []
    
    this.headers.forEach(header => {
      const lowerHeader = header.toLowerCase()
      
      // Check for source language indicators
      if (sourcePatterns.some(pattern => lowerHeader.includes(pattern))) {
        if (!this.sourceLanguage) {
          // Try to extract language code from header
          const match = header.match(languagePattern)
          this.sourceLanguage = match ? match[0] : 'source'
        }
      }
      
      // Extract language codes from headers
      const langMatch = header.match(languagePattern)
      if (langMatch && !this.allLanguages.includes(langMatch[0])) {
        this.allLanguages.push(langMatch[0])
        if (langMatch[0] !== this.sourceLanguage && !this.targetLanguages.includes(langMatch[0])) {
          this.targetLanguages.push(langMatch[0])
        }
      }
    })
    
    // Fallback: treat first column as source if no explicit source found
    if (!this.sourceLanguage && this.headers.length > 0) {
      this.sourceLanguage = this.headers[0]
    }
    
    // If no languages detected, use all headers as potential languages
    if (this.allLanguages.length === 0) {
      this.allLanguages = [...this.headers]
      this.targetLanguages = this.headers.slice(1) // All except first
    }
  }

  /**
   * Initialize visible languages (all languages except source by default)
   */
  _initializeVisibleLanguages() {
    this.visibleLanguages = this.sourceLanguage 
      ? [this.sourceLanguage, ...this.targetLanguages]
      : [...this.allLanguages]
  }

  /**
   * Load settings from persistent storage
   */
  _loadFromSettings() {
    if (!this.settingsManager || !this.jobId) return

    // Initialize job settings with current language info
    const languageInfo = this.getLanguageInfo()
    const jobSettings = this.settingsManager.initializeJobSettings(this.jobId, languageInfo)

    // Apply saved settings
    if (jobSettings.visibleLanguages && jobSettings.visibleLanguages.length > 0) {
      // Filter to only include languages that actually exist in the data
      const validLanguages = jobSettings.visibleLanguages.filter(lang => 
        this.allLanguages.includes(lang)
      )
      if (validLanguages.length > 0) {
        this.visibleLanguages = validLanguages
      }
    }

    if (jobSettings.currentPage && jobSettings.currentPage > 0) {
      this.currentPage = jobSettings.currentPage
    }

    if (jobSettings.searchValue) {
      this.searchFilter = jobSettings.searchValue
    }

    console.log('📥 Settings loaded for job', this.jobId, {
      visibleLanguages: this.visibleLanguages.length,
      currentPage: this.currentPage,
      searchFilter: this.searchFilter
    })
  }

  /**
   * Save current state to settings
   */
  _saveToSettings() {
    if (!this.settingsManager || !this.jobId) return

    this.settingsManager.saveJobSettings(this.jobId, {
      visibleLanguages: [...this.visibleLanguages],
      currentPage: this.currentPage,
      searchValue: this.searchFilter
    })
  }

  /**
   * Get paginated data for current view
   */
  getPaginatedData() {
    if (!this.isLoaded) {
      return {
        data: [],
        pagination: this.getPaginationInfo(),
        languages: this.getLanguageInfo()
      }
    }
    
    // Apply search filter if present
    let filteredData = this.processedData
    if (this.searchFilter) {
      const searchLower = this.searchFilter.toLowerCase()
      filteredData = this.processedData.filter(row =>
        Object.values(row).some(value => 
          String(value).toLowerCase().includes(searchLower)
        )
      )
    }
    
    // Calculate pagination
    const startIndex = (this.currentPage - 1) * this.itemsPerPage
    const endIndex = startIndex + this.itemsPerPage
    const paginatedData = filteredData.slice(startIndex, endIndex)
    
    // Filter columns by visible languages
    const visibleHeaders = this.headers.filter(header => 
      this.visibleLanguages.includes(header) || 
      this.visibleLanguages.some(lang => header.includes(lang))
    )
    
    return {
      data: paginatedData,
      headers: visibleHeaders,
      pagination: {
        ...this.getPaginationInfo(),
        totalFiltered: filteredData.length
      },
      languages: this.getLanguageInfo()
    }
  }

  /**
   * Get pagination information
   */
  getPaginationInfo() {
    const totalPages = Math.ceil(this.totalRows / this.itemsPerPage)
    
    return {
      currentPage: this.currentPage,
      itemsPerPage: this.itemsPerPage,
      totalRows: this.totalRows,
      totalPages,
      hasNextPage: this.currentPage < totalPages,
      hasPreviousPage: this.currentPage > 1,
      startRow: (this.currentPage - 1) * this.itemsPerPage + 1,
      endRow: Math.min(this.currentPage * this.itemsPerPage, this.totalRows)
    }
  }

  /**
   * Get language information
   */
  getLanguageInfo() {
    return {
      sourceLanguage: this.sourceLanguage,
      targetLanguages: this.targetLanguages,
      allLanguages: this.allLanguages,
      visibleLanguages: this.visibleLanguages,
      hiddenLanguages: this.allLanguages.filter(lang => !this.visibleLanguages.includes(lang))
    }
  }

  /**
   * Navigation methods
   */
  goToPage(page) {
    const totalPages = Math.ceil(this.totalRows / this.itemsPerPage)
    this.currentPage = Math.max(1, Math.min(page, totalPages))
    this._saveToSettings()
    return this.getPaginatedData()
  }

  nextPage() {
    return this.goToPage(this.currentPage + 1)
  }

  previousPage() {
    return this.goToPage(this.currentPage - 1)
  }

  setItemsPerPage(items) {
    const newItemsPerPage = Math.max(5, Math.min(100, items))
    this.itemsPerPage = newItemsPerPage
    this.currentPage = 1 // Reset to first page
    
    // Save user preference for items per page
    if (this.settingsManager) {
      this.settingsManager.setItemsPerPage(newItemsPerPage)
    }
    
    this._saveToSettings()
    return this.getPaginatedData()
  }

  /**
   * Language filtering methods
   */
  setVisibleLanguages(languages) {
    this.visibleLanguages = languages.filter(lang => this.allLanguages.includes(lang))
    this._saveToSettings()
    return this.getPaginatedData()
  }

  showAllLanguages() {
    this.visibleLanguages = [...this.allLanguages]
    this._saveToSettings()
    return this.getPaginatedData()
  }

  hideLanguage(language) {
    this.visibleLanguages = this.visibleLanguages.filter(lang => lang !== language)
    this._saveToSettings()
    return this.getPaginatedData()
  }

  showLanguage(language) {
    if (this.allLanguages.includes(language) && !this.visibleLanguages.includes(language)) {
      this.visibleLanguages.push(language)
    }
    this._saveToSettings()
    return this.getPaginatedData()
  }

  /**
   * Search functionality
   */
  setSearchFilter(searchTerm) {
    this.searchFilter = searchTerm
    this.currentPage = 1 // Reset to first page when searching
    this._saveToSettings()
    return this.getPaginatedData()
  }

  clearSearch() {
    this.searchFilter = ''
    this._saveToSettings()
    return this.getPaginatedData()
  }

  /**
   * Export filtered data
   */
  exportFilteredData(format = 'csv') {
    if (!this.isLoaded) {
      throw new Error('No data loaded')
    }
    
    const visibleHeaders = this.headers.filter(header => 
      this.visibleLanguages.includes(header) || 
      this.visibleLanguages.some(lang => header.includes(lang))
    )
    
    let filteredData = this.processedData
    if (this.searchFilter) {
      const searchLower = this.searchFilter.toLowerCase()
      filteredData = this.processedData.filter(row =>
        Object.values(row).some(value => 
          String(value).toLowerCase().includes(searchLower)
        )
      )
    }
    
    // Extract only visible columns
    const exportData = filteredData.map(row => {
      const exportRow = {}
      visibleHeaders.forEach(header => {
        exportRow[header] = row[header] || ''
      })
      return exportRow
    })
    
    if (format === 'csv') {
      return Papa.unparse(exportData)
    } else if (format === 'json') {
      return JSON.stringify(exportData, null, 2)
    } else {
      throw new Error(`Unsupported export format: ${format}`)
    }
  }

  /**
   * Performance and debugging information
   */
  getPerformanceInfo() {
    return {
      isLoaded: this.isLoaded,
      totalRows: this.totalRows,
      totalColumns: this.headers.length,
      loadTime: this.loadTime,
      processTime: this.processTime,
      memoryUsage: this._estimateMemoryUsage()
    }
  }

  _estimateMemoryUsage() {
    if (!this.isLoaded) return 0
    
    // Rough estimate: each row object + string data
    const avgRowSize = JSON.stringify(this.processedData[0] || {}).length
    return (avgRowSize * this.totalRows) / 1024 // KB
  }

  /**
   * Reset processor state
   */
  reset() {
    this.rawData = []
    this.processedData = []
    this.headers = []
    this.totalRows = 0
    this.isLoaded = false
    this.sourceLanguage = null
    this.targetLanguages = []
    this.allLanguages = []
    this.currentPage = 1
    this.visibleLanguages = []
    this.searchFilter = ''
    this.loadTime = null
    this.processTime = null
  }
}

export default TranslationFileProcessor